/**
 * `<ChatComposer>` — Architect-visual composer primitive (Y bundle / spec §10.1.a).
 *
 * Combines: textarea + auto-resize + shared keyboard handler + Send/Cancel
 * button label state machine. Replaces the per-surface-rolled composer JSX
 * that diverged across 9 chat surfaces in the Phase 0 audit (§3.3).
 *
 * **Send/Cancel state machine** (Y bundle / spec §10.1.b):
 * - `inFlight: false` → button label "Send"; click fires `onSend`. Disabled
 *   when draft is empty or surface-side `disabledExtra` is true (e.g.
 *   reactive-viz disables when no upstream edge connected).
 * - `inFlight: true` → button label "Cancel"; click fires `onCancel`. Always
 *   enabled while in flight — that's the whole point (deadlock escape).
 * - After a Cancel click the button shows a disabled "Canceling…" until the
 *   run unwinds (`inFlight` → false). Cancel resolves server-side: the abort
 *   endpoint signals the handler, whose catch persists the "Canceled by you"
 *   message and resolves the original send — that round trip takes a beat,
 *   and without acknowledgment the click looks like it did nothing. If the
 *   run is somehow still in flight 10s later (abort request lost), the
 *   button re-enables so the escape hatch isn't lost.
 *
 * `inFlight` is `chatBusy || replyPending` from the surface's perspective:
 * either an active local send is mid-stream OR the trailing message is a
 * user role waiting for a server reply.
 *
 * Why a single button that flips label rather than two side-by-side buttons:
 * matches the user's mental model — at any moment there's exactly one action
 * to take (send something, or stop something). Two buttons would imply both
 * are valid simultaneously, which they aren't.
 */

import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { onAiChatComposerKeyDown } from "../../lib/aiChatComposerKeyDown.js";
import { useAutoResizeTextarea } from "../../hooks/useAutoResizeTextarea.js";

export type ChatComposerProps = {
  draft: string;
  setDraft: (value: string) => void;
  onSend: () => void;
  onCancel: () => void;
  /** True when an agent run is in flight (chatBusy or replyPending). */
  inFlight: boolean;
  /** Composer placeholder text. Defaults to a generic prompt. */
  placeholder?: string;
  /**
   * Surface-specific extra-disabled signal for the Send button (e.g.
   * reactive-viz disables when no incoming data edge is connected). When
   * true, Send is disabled even with non-empty draft. Doesn't affect Cancel.
   */
  disabledExtra?: boolean;
  /**
   * Title attribute applied to the Send button when `disabledExtra` is true.
   * Helps users understand why Send is grayed out.
   */
  disabledExtraTitle?: string;
  /** Min/max rows for the auto-resizing textarea. Defaults match Architect. */
  minRows?: number;
  maxRows?: number;
};

export function ChatComposer(props: ChatComposerProps): JSX.Element {
  const {
    draft,
    setDraft,
    onSend,
    onCancel,
    inFlight,
    placeholder = "Type a message…",
    disabledExtra = false,
    disabledExtraTitle,
    minRows = 2,
    maxRows = 12,
  } = props;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useAutoResizeTextarea(textareaRef, draft, { minRows, maxRows });

  const [cancelPending, setCancelPending] = useState(false);

  useEffect(() => {
    if (!inFlight) setCancelPending(false);
  }, [inFlight]);

  // Escape-hatch guard: if the abort request was lost (hub restart wiped the
  // in-memory registry, network drop), re-enable Cancel so the user can retry.
  useEffect(() => {
    if (!cancelPending) return;
    const handle = setTimeout(() => setCancelPending(false), 10_000);
    return () => clearTimeout(handle);
  }, [cancelPending]);

  const canSend = !inFlight && !!draft.trim() && !disabledExtra;

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    onAiChatComposerKeyDown(e, {
      canSubmit: canSend,
      onSubmit: onSend,
    });
  };

  return (
    <div className="inputWithButton">
      <textarea
        ref={textareaRef}
        className="input"
        rows={minRows}
        placeholder={placeholder}
        value={draft}
        disabled={inFlight}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        style={{ resize: "none", overflowY: "auto" }}
      />
      {inFlight ? (
        <button
          type="button"
          className="btn btnSm"
          disabled={cancelPending}
          onClick={() => {
            setCancelPending(true);
            onCancel();
          }}
          title={cancelPending ? "Cancel requested — waiting for the agent to stop." : "Stop the agent and reset the composer."}
        >
          {cancelPending ? "Canceling…" : "Cancel"}
        </button>
      ) : (
        <button
          type="button"
          className="btn btnSm btnPrimary"
          disabled={!canSend}
          onClick={onSend}
          title={disabledExtra ? disabledExtraTitle : undefined}
        >
          Send
        </button>
      )}
    </div>
  );
}
